import { GRID_COLUMNS, type LayoutItem } from '@vision360/contracts';
import { GripVertical, Settings2, X } from 'lucide-react';
import { type KeyboardEvent, useId, useState } from 'react';
import ReactGridLayout, { type Layout, useContainerWidth } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { widgetName, widgetRegistry } from '@/widgets/_registry';
import { WidgetFrame } from '@/widgets/_sdk/frame';
import type { AnyWidget } from '@/widgets/_sdk/types';
import { keyboardMove, keyboardResize, mergeGeometry, readingOrder, toGrid } from './layout';

/**
 * The dashboard grid (PRD VS-041, TechSpec §9.5): react-grid-layout for the
 * mouse, arrows / Shift+arrows / Enter / Delete for the keyboard (rule 6).
 * Under 768 px it is a read-only single column.
 */

export const ROW_HEIGHT = 80;
const MARGIN = 12;
export const NARROW_PX = 768;

export interface DashboardGridProps {
  items: LayoutItem[];
  editing: boolean;
  /** Undoable change. A mouse gesture commits once, at its end: one undo step. */
  onChange: (items: LayoutItem[]) => void;
  onConfigure: (id: string) => void;
  onRemove: (id: string) => void;
  registry?: ReadonlyMap<string, AnyWidget>;
}

const label = (item: LayoutItem) =>
  (typeof item.widget.props['label'] === 'string' && item.widget.props['label']) ||
  widgetName(item.widget.type);

export function DashboardGrid({
  items,
  editing,
  onChange,
  onConfigure,
  onRemove,
  registry = widgetRegistry,
}: DashboardGridProps) {
  const { t } = useTranslation('dashboards');
  const { width, containerRef, mounted } = useContainerWidth();
  const [announcement, setAnnouncement] = useState('');
  const helpId = useId();
  // 0 = not laid out (hidden, or a test DOM): treat as wide rather than collapse.
  const narrow = mounted && width > 0 && width < NARROW_PX;

  const onKeyDown = (item: LayoutItem) => (event: KeyboardEvent<HTMLDivElement>) => {
    if (!editing || event.target !== event.currentTarget) return;
    const arrows: Record<string, [number, number]> = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    };
    const delta = arrows[event.key];
    if (delta) {
      event.preventDefault();
      const next = event.shiftKey
        ? keyboardResize(items, item.i, delta[0], delta[1], registry)
        : keyboardMove(items, item.i, delta[0], delta[1], registry);
      if (next === items) return;
      onChange(next);
      const moved = next.find((i) => i.i === item.i);
      if (moved) {
        setAnnouncement(
          t('widget.moved', {
            name: label(item),
            x: moved.x + 1,
            y: moved.y + 1,
            w: moved.w,
            h: moved.h,
          }),
        );
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();
      onConfigure(item.i);
    } else if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      onRemove(item.i);
    }
  };

  const actions = (item: LayoutItem) =>
    editing ? (
      <div className="flex shrink-0 items-center gap-0.5">
        <span
          className="v360-drag cursor-grab rounded-sm p-1 text-muted-foreground hover:bg-muted active:cursor-grabbing"
          aria-hidden="true"
        >
          <GripVertical className="h-4 w-4" />
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          aria-label={t('widget.configure', { name: label(item) })}
          onClick={() => onConfigure(item.i)}
        >
          <Settings2 aria-hidden="true" className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          aria-label={t('widget.remove', { name: label(item) })}
          onClick={() => onRemove(item.i)}
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </Button>
      </div>
    ) : null;

  const frame = (item: LayoutItem) => (
    <WidgetFrame
      type={item.widget.type}
      props={item.widget.props}
      size={{ w: item.w, h: item.h }}
      actions={actions(item)}
      onConfigure={() => onConfigure(item.i)}
      registry={registry}
    />
  );

  return (
    <div ref={containerRef}>
      {editing && (
        <p id={helpId} className="mb-2 text-xs text-muted-foreground">
          {narrow ? t('readOnlyNarrow') : t('keyboardHelp')}
        </p>
      )}
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>
      {narrow ? (
        <ul className="space-y-3">
          {readingOrder(items).map((item) => (
            <li key={item.i} style={{ height: item.h * ROW_HEIGHT + (item.h - 1) * MARGIN }}>
              {frame(item)}
            </li>
          ))}
        </ul>
      ) : (
        mounted && (
          <ReactGridLayout
            width={width || 1280}
            layout={toGrid(items, registry)}
            gridConfig={{
              cols: GRID_COLUMNS,
              rowHeight: ROW_HEIGHT,
              margin: [MARGIN, MARGIN],
              containerPadding: [0, 0],
            }}
            dragConfig={{ enabled: editing, handle: '.v360-drag' }}
            resizeConfig={{ enabled: editing, handles: ['se'] }}
            onDragStop={(layout: Layout) => onChange(mergeGeometry(items, layout))}
            onResizeStop={(layout: Layout) => onChange(mergeGeometry(items, layout))}
          >
            {items.map((item) => (
              // biome-ignore lint/a11y/useSemanticElements: react-grid-layout positions its own div children; a fieldset cannot be one
              <div
                key={item.i}
                tabIndex={editing ? 0 : undefined}
                role="group"
                aria-label={editing ? t('widget.move', { name: label(item) }) : label(item)}
                aria-describedby={editing ? helpId : undefined}
                onKeyDown={onKeyDown(item)}
                className="rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                {frame(item)}
              </div>
            ))}
          </ReactGridLayout>
        )
      )}
    </div>
  );
}
